import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { doc, getDoc } from 'firebase/firestore'
import { projectFirestore } from '@/firebase/config'

export function useSatisfactionSurvey() {
  const store = useStore()
  
  const surveyOpen = ref(false)
  const hasCheckedSurvey = ref(false)
  const userHasCompletedSurvey = ref(false)

  const currentUser = computed(() => store.state.currentUser)

  // Configuration for survey timing
  const SURVEY_DELAY_DAYS = 20 // Show survey after 20 days of account creation

  // Get user's account age in days
  const getUserAccountAge = () => {
    if (!currentUser.value?.metadata?.creationTime) {
      return 0
    }

    const creationTime = new Date(currentUser.value.metadata.creationTime)
    const now = new Date()
    const daysSinceCreation = (now - creationTime) / (1000 * 60 * 60 * 24)
    
    return Math.floor(daysSinceCreation)
  }

  // Check if user has been using the system long enough
  const hasUserBeenActiveLongEnough = () => {
    const accountAge = getUserAccountAge()
    const isActiveLongEnough = accountAge >= SURVEY_DELAY_DAYS
    
    return isActiveLongEnough
  }

  // Check if user has completed the survey
  const checkSurveyCompletion = async () => {
    
    if (!currentUser.value) {
      return
    }

    try {
      const userRef = doc(projectFirestore, 'users', currentUser.value.uid)
      const userDoc = await getDoc(userRef)
      
      if (userDoc.exists()) {
        const userData = userDoc.data()
        // Check if user has completed the survey
        const surveys = userData.surveys || {}
        const surveyKey = 'satisfaction_survey'
        const hasCompletedSurvey = surveys[surveyKey]?.completed || false
        
        
        userHasCompletedSurvey.value = hasCompletedSurvey
        
        // If user has completed survey, close it
        if (hasCompletedSurvey && surveyOpen.value) {
          surveyOpen.value = false
        }
      }
    } catch (error) {
      console.error('Error checking survey completion:', error)
    } finally {
      hasCheckedSurvey.value = true
    }
  }

  // Show survey if user hasn't completed it and has been active long enough
  const showSurveyIfNeeded = async () => {
    
    if (!currentUser.value) return

    // Always check survey completion first, regardless of environment
    await checkSurveyCompletion()
    
    // Check if user has been active long enough
    const userActiveLongEnough = hasUserBeenActiveLongEnough()
    
    // Check if we're on a dashboard page (more flexible check)
    const isOnDashboard = store.state.currentDashboardView?.dashboard || 
                         window.location.pathname.includes('/dashboard') ||
                         window.location.pathname === '/' ||
                         window.location.pathname.includes('/backoffice')
    
    // Show survey if user hasn't completed it, has been active long enough, and we're on the dashboard
    if (!userHasCompletedSurvey.value && userActiveLongEnough && isOnDashboard) {
      // Wait a bit for the page to load, then show survey
      setTimeout(() => {
        surveyOpen.value = true
      }, 3000) // Show after 3 seconds
    } 
  }

  // Close survey
  const closeSurvey = () => {
    surveyOpen.value = false
  }

  // Manual test function for debugging
  const testSurveySystem = async () => {
    
    // Force a fresh check
    hasCheckedSurvey.value = false
    await showSurveyIfNeeded()
  }

  // Force show survey (for testing)
  const forceShowSurvey = () => {
    surveyOpen.value = true
  }

  // Check survey on mount and when user changes
  onMounted(() => {
    if (currentUser.value) showSurveyIfNeeded()
  })

  // Watch for user changes
  watch(currentUser, async (newUser) => {
    if (newUser) {
      await showSurveyIfNeeded()
    }
  })

  // Expose test functions globally for debugging
  if (typeof window !== 'undefined') {
    window.testSurveySystem = testSurveySystem
    window.forceShowSurvey = forceShowSurvey
    window.getUserAccountAge = getUserAccountAge
  }

  return {
    surveyOpen,
    userHasCompletedSurvey,
    hasCheckedSurvey,
    showSurveyIfNeeded,
    closeSurvey,
    checkSurveyCompletion,
    refreshCompletionStatus: checkSurveyCompletion,
    hasUserBeenActiveLongEnough,
    getUserAccountAge,
    testSurveySystem,
    forceShowSurvey
  }
} 